import { signalObject, type SignalObject } from "omi";
import isEmpty from "lodash-es/isEmpty";

import { storageSignal } from "@/utils/signals/storageSignal.ts";
import { type Project } from "@/api";
import { openModal } from "@/wc/components";
import { Store as GlobalStore } from "@/store";

import { MadeForYouStore } from "../features/madeForYou/store";
import { DirectoryView } from "../views";
import { useMyProjects } from "./queries/useMyProjects.ts";
import { useProjects } from "./queries/useProjects.ts";
import { useSchedule } from "./queries/useSchedule.ts";
import { useSearch } from "./queries/useSearch.ts";
import { useUpdateSelectedProjectsMutation } from "./mutations";

type Store = {
  activeView: DirectoryView;
  onboardingModeActive: boolean;
  recommendedExpanded: boolean;
  viewingCourse: string | undefined;
};

type Actions = {
  show: (view: DirectoryView) => void;
  search: (query: string) => Promise<void>;
  updateRoadmap: (projectId: string[], selected: boolean, onError?: () => void) => void;
  isOnboardingModeActive: () => boolean;
  isRecommendedExpanded: () => boolean;
  setViewingCourse: (courseId: string | undefined) => void;
};

export type DirectoryStore = SignalObject<Store> & Actions;

function createDirectoryStore(): DirectoryStore {
  const initialState: Omit<Store, "activeView"> = {
    onboardingModeActive: !GlobalStore.user.isLoggedIn.value,
    recommendedExpanded: false,
    viewingCourse: undefined,
  };

  const activeView = storageSignal<DirectoryView>("directoryView", {
    storageType: "local",
    default: DirectoryView.EXPLORE,
  });

  const directoryStore = { ...signalObject(initialState), activeView };
  const projectsSlice = useProjects();
  const myProjectsSlice = useMyProjects();
  const searchSlice = useSearch();
  const scheduleSlice = useSchedule();

  return {
    ...directoryStore,
    isOnboardingModeActive: isOnboardingModeActiveSelector(GlobalStore),
    isRecommendedExpanded: isRecommendedExpandedSelector(directoryStore),
    setViewingCourse: setViewingCourseSelector(directoryStore),
    show: showFunc(directoryStore),
    search: searchFunc(directoryStore, searchSlice),
    updateRoadmap: updateRoadmapFunc(
      projectsSlice,
      myProjectsSlice,
      searchSlice,
      scheduleSlice,
      MadeForYouStore,
      GlobalStore,
    ),
  };
}

function showFunc(directoryStore: SignalObject<Store>): (view: DirectoryView) => void {
  return (view: DirectoryView) => {
    directoryStore.activeView.value = view;
  };
}

function searchFunc(
  directoryStore: SignalObject<Store>,
  searchSlice: ReturnType<typeof useSearch>,
): (query: string) => Promise<void> {
  const setViewingCourse = setViewingCourseSelector(directoryStore);
  return async (query: string) => {
    if (query === directoryStore.viewingCourse.value) {
      return;
    }

    directoryStore.viewingCourse.value = undefined;

    if (directoryStore.activeView.value !== DirectoryView.EXPLORE) {
      showFunc(directoryStore)(DirectoryView.EXPLORE);
    }

    const res = await searchSlice.setSearchQuery(query);
    if (searchSlice.isPrefixSearch("series:") && !isEmpty(res)) {
      // If the search is for a series, set the viewing course to the first result
      setViewingCourse(res[0].title);
    }
  };
}

function isOnboardingModeActiveSelector(globalStore: GlobalStore): () => boolean {
  return () => !globalStore.user.isLoggedIn.value;
}

function isRecommendedExpandedSelector(directoryStore: SignalObject<Store>): () => boolean {
  return () => directoryStore.recommendedExpanded.value;
}

function setViewingCourseSelector(directoryStore: SignalObject<Store>): (courseId: string | undefined) => void {
  return (courseId: string | undefined) => {
    directoryStore.viewingCourse.value = courseId;

    if (courseId && directoryStore.activeView.value !== DirectoryView.EXPLORE) {
      directoryStore.activeView.value = DirectoryView.EXPLORE;
    }
  };
}

/**
 * This function updates the selected status of a project across:
 * - Projects (useProjects)
 * - My Projects (useMyProjects)
 * - Current search results (useSearch)
 */
function updateRoadmapFunc(
  projectsSlice: ReturnType<typeof useProjects>,
  myProjectsSlice: ReturnType<typeof useMyProjects>,
  searchSlice: ReturnType<typeof useSearch>,
  scheduleSlice: ReturnType<typeof useSchedule>,
  madeForYouStore: typeof MadeForYouStore,
  globalStore: GlobalStore,
): (id: string[], selected: boolean, onError?: () => void) => void {
  if (globalStore.user.isLoggedIn.value === false) {
    return () => {
      openModal("login", {});
    };
  }

  const updateMutation = useUpdateSelectedProjectsMutation();

  const add = (ids: string[], projects: Project[]) => {
    projectsSlice.selectProject(ids);
    myProjectsSlice.addToRoadmap(projects);
    searchSlice.updateSelected(ids, true);
    madeForYouStore.updateSelected(ids, true);
  };

  const remove = (ids: string[]) => {
    projectsSlice.unselectProject(ids);
    myProjectsSlice.removeFromRoadmap(ids);
    searchSlice.updateSelected(ids, false);
    madeForYouStore.updateSelected(ids, false);
  };

  return (projectIds: string[], selected: boolean, onError?: () => void) => {
    scheduleSlice.isLoading.value = true;

    const projects = projectIds.map(projectsSlice.getProject).filter(p => p != null) as Project[];
    if (selected) {
      add(projectIds, projects);
    } else {
      remove(projectIds);
    }

    if (!myProjectsSlice.data.value) {
      return;
    }

    updateMutation.mutate(myProjectsSlice.data.value, {
      onSuccess: () => {
        scheduleSlice.fetch({});
      },
      onError: () => {
        if (selected) {
          remove(projectIds);
        } else {
          add(projectIds, projects);
        }

        onError?.();
      },
    });
  };
}

export const DirectoryStore = createDirectoryStore();
